import { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Plus, 
  Edit2, 
  Save,
  X,
  Trash2} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { RelayDto } from '@/lib/api/types.gen';
import { FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { 
  CreateRelayMut, 
  UpdateRelayMut, 
  DeleteRelayMut,
  type CreateRelayInput,
  type UpdateRelayInput 
} from '@/lib/api/monitoring/relays';
import { DialogDescription } from '@radix-ui/react-dialog';


interface RelayComponentsModalProps {
  deviceId: string; // Parent device ID
  subDeviceId: string; // Sub-device (relay device) ID
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}

export const RelayComponentsModal = ({
  deviceId,
  subDeviceId,
  open,
  onOpenChange,
  onClose,
}: RelayComponentsModalProps) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  const queryClient = useQueryClient();
  
  // Fetch fresh device data
  const { data: subDevices } = useQuery(FetchSubDevicesQuery({ deviceId }));
  const device = subDevices?.find(d => d.id === subDeviceId);
  const relays = device?.relays || [];

  const createRelayMut = useMutation({
    ...CreateRelayMut(),
    onMutate: async (newRelay: CreateRelayInput) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((device: any) => {
          if (device.id === subDeviceId) {
            return {
              ...device,
              relays: [
                ...(device.relays || []),
                {
                  id: `temp_${Date.now()}`, // Temporary ID
                  name: newRelay.name
                }
              ]
            };
          }
          return device;
        });
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, _newRelay, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to create component',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setIsAdding(false);
      setNewName('');
      toast({
        title: 'Component created',
        description: 'Relay component has been created successfully.',
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const updateRelayMut = useMutation({
    ...UpdateRelayMut(),
    onMutate: async (updatedRelay: UpdateRelayInput) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((device: any) => {
          if (device.id === subDeviceId) {
            return {
              ...device,
              relays: (device.relays || []).map((relay: any) => 
                relay.id === updatedRelay.id 
                  ? { ...relay, name: updatedRelay.name }
                  : relay
              )
            };
          }
          return device;
        });
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, _updatedRelay, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to update component',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      setEditingId(null);
      setEditName('');
      toast({
        title: 'Component updated',
        description: 'Relay component has been updated successfully.',
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const deleteRelayMut = useMutation({
    ...DeleteRelayMut(),
    onMutate: async (deletedRelay: { id: string }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['device.secondary', deviceId] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['device.secondary', deviceId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['device.secondary', deviceId], (old: any) => {
        if (!old) return old;
        
        return old.map((device: any) => {
          if (device.id === subDeviceId) {
            return {
              ...device,
              relays: (device.relays || []).filter((relay: any) => relay.id !== deletedRelay.id)
            };
          }
          return device;
        });
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (err, _deletedRelay, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['device.secondary', deviceId], context?.previousData);
      toast({
        title: 'Failed to delete component',
        description: err.message || 'Something went wrong.',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'Component deleted',
        description: 'Relay component has been deleted successfully.',
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['device.secondary', deviceId] });
    },
  });

  const startEditing = (relay: RelayDto) => {
    setEditingId(relay.id || null);
    setEditName(relay.name || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = (relayId: string) => {
    const current = relays.find((r: any) => r.id === relayId) || {};
    const input: UpdateRelayInput = {
      id: relayId,
      name: editName.trim(),
      componentGroupId: current.componentGroupId || subDeviceId,
      relatedComponentGroups: current.relatedComponentGroups || [],
    } as UpdateRelayInput;
    updateRelayMut.mutate(input);
  };

  const startAdding = () => {
    setIsAdding(true);
    setNewName('');
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewName('');
  };

  const saveNew = () => {
    if (newName.trim()) {
      const input: CreateRelayInput = {
        name: newName.trim(),
        componentGroupId: subDeviceId // Using sub-device ID as componentGroupId
      };
      createRelayMut.mutate(input);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      deleteRelayMut.mutate({ id: deleteConfirm.id });
      setDeleteConfirm(null);
    }
  };

  const RelayRow = ({ relay }: { relay: RelayDto }) => {
    const isEditing = editingId === relay.id;

    return (
      <TableRow>
        <TableCell>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-8 w-48"
                autoFocus
                onBlur={(e) => e.preventDefault()}
              />
            ) : (
              <span className="font-medium">{relay.name}</span>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          {isEditing ? (
            <div className="flex gap-1 justify-end">
              <Button
                size="sm"
                onClick={() => saveEdit(relay.id || '')}
                disabled={updateRelayMut.isPending}
                className="bg-lime text-black hover:bg-lime/80"
              >
                <Save className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEditing}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-1 justify-end">
              <Button
                size="sm"
                onClick={() => startEditing(relay)}
                className="bg-lime text-black hover:bg-lime/80"
              >
                <Edit2 className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDeleteConfirm({ id: relay.id || '', name: relay.name || '' })}
                disabled={deleteRelayMut.isPending}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Relay Components - {device?.name || 'Unknown Device'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 px-6">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mt-4">
              <p className="text-sm text-gray-300 leading-relaxed">
                Relays will be automatically populated when supported by your device integration. 
                If your integration doesn't automatically detect relays, you can manually add them here.
              </p>
            </div>
            
            <div className="max-w-2xl">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {relays.map((relay) => (
                  <RelayRow key={relay.id} relay={relay} />
                ))}
                {isAdding && (
                  <TableRow>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Input
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          placeholder="Enter component name"
                          className="h-8 flex-1"
                          autoFocus
                          onBlur={(e) => e.preventDefault()}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          onClick={saveNew}
                          disabled={createRelayMut.isPending}
                          className="bg-lime text-black hover:bg-lime/80"
                        >
                          <Save className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelAdding}
                          className="text-gray-400 hover:text-white"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!isAdding && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center py-6">
                      <Button 
                        variant="ghost" 
                        onClick={startAdding}
                        className="text-lime hover:text-lime/80 hover:bg-lime/10"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Relay Component
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Relay Component</DialogTitle>
            <DialogDescription className="text-gray-400 space-y-2">
              <p>Are you sure you want to delete <span className="font-medium text-white">"{deleteConfirm?.name}"</span>?</p>
              <p>This action cannot be undone.</p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
};
